import React, { useEffect, useRef, useState } from 'react';
import { MailCheck, RefreshCw } from 'lucide-react';
import { Lang } from '@/data/constants';
import { RESEND_COOLDOWN_SECONDS, resendConfirmation } from '@/lib/accountState';

/**
 * RESEND CONFIRMATION EMAIL — one component, used on BOTH the registration
 * confirmation screen and the sign-in page, so the behaviour, the cooldown and
 * the wording can never drift between the two places.
 *
 * • 60-second cooldown, enforced client-side here AND server-side by
 *   account-verification (3/hour/user, 10/hour/address).
 * • Bilingual success and failure feedback — including the honest case where
 *   the address is already confirmed, in which case the account email is sent
 *   instead and the user is told they can sign in right now.
 */
interface Props {
  email: string;
  lang: Lang;
  userId?: string;
  name?: string;
  /** 'panel' = full-width block (register screen), 'inline' = compact (login). */
  variant?: 'panel' | 'inline';
  className?: string;
}

const ResendConfirmation: React.FC<Props> = ({
  email,
  lang,
  userId,
  name,
  variant = 'panel',
  className = '',
}) => {
  const es = lang === 'es';
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (left <= 0) return;
    timer.current = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [left]);

  const valid = /^\S+@\S+\.\S+$/.test(email || '');

  const send = async () => {
    if (busy || left > 0) return;
    setNote('');
    setError('');
    if (!valid) {
      setError(es ? 'Ingrese un correo electrónico válido.' : 'Enter a valid email address.');
      return;
    }
    setBusy(true);
    const res = await resendConfirmation(email, lang, userId, name);
    setBusy(false);

    if (res.ok) {
      // Start the cooldown only on a real send.
      setLeft(RESEND_COOLDOWN_SECONDS);
      setNote(
        res.alreadyConfirmed
          ? es
            ? `Su correo ya está confirmado. Le enviamos la confirmación de su cuenta a ${email} — ya puede iniciar sesión.`
            : `Your email is already confirmed. We sent your account confirmation to ${email} — you can sign in now.`
          : es
          ? `Enviamos un nuevo enlace de confirmación a ${email}. Revise también la carpeta de spam.`
          : `We sent a new confirmation link to ${email}. Please check your spam folder too.`,
      );
      return;
    }

    if (res.rateLimited) {
      const mins = Math.max(1, Math.ceil((res.retryAfter || 3600) / 60));
      setLeft(Math.min(res.retryAfter || RESEND_COOLDOWN_SECONDS, RESEND_COOLDOWN_SECONDS));
      setError(
        es
          ? `Demasiados envíos. Intente de nuevo en ${mins} minuto(s) o escriba a support@giggone.com.`
          : `Too many sends. Try again in ${mins} minute(s), or email support@giggone.com.`,
      );
      return;
    }

    setError(
      es
        ? 'No pudimos enviar el correo en este momento. Intente de nuevo o escriba a support@giggone.com.'
        : 'We could not send the email right now. Please try again, or email support@giggone.com.',
    );
  };

  const label = busy
    ? es
      ? 'Enviando…'
      : 'Sending…'
    : left > 0
    ? es
      ? `Reenviar en ${left}s`
      : `Resend in ${left}s`
    : es
    ? 'Reenviar correo de confirmación'
    : 'Resend confirmation email';

  const btn =
    variant === 'panel'
      ? 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border-2 border-emerald-400 bg-white px-5 py-3 text-sm font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50'
      : 'inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50';

  return (
    <div className={className}>
      <button type="button" onClick={send} disabled={busy || left > 0} className={btn}>
        {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}
        {label}
      </button>
      {note && (
        <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm leading-relaxed text-emerald-800">{note}</p>
      )}
      {error && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm leading-relaxed text-red-700">{error}</p>}
      {left > 0 && !error && (
        <p className="mt-1.5 text-xs text-slate-500">
          {es
            ? 'Puede volver a enviarlo una vez por minuto.'
            : 'You can send it again once per minute.'}
        </p>
      )}
    </div>
  );
};

export default ResendConfirmation;
